import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { SectionCard } from '@/components/common/section-card';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/fleet/states';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePublishListing } from '@/hooks/use-publish-listing';
import { useStoreListings } from '@/hooks/use-store-listings';
import type { PublishOptions, StoreListing, StorePlatform } from '@/types/store';

import { ListingDrawer } from './listing-drawer';
import { ListingStatusBadge } from './store-badges';
import { NewListingDialog } from './new-listing-dialog';
import { notifyPublishError } from './publish-feedback';
import { PublishConfirmDialog } from './publish-confirm-dialog';
import { PLATFORM_LABELS } from './store-constants';

/** A short summary line for the table — first non-empty of the platform's key fields. */
function summaryFor(listing: StoreListing): string {
  // `find` (not `??`) so an empty-string field is skipped, not treated as set.
  const candidate = [
    listing.title,
    listing.promotionalText,
    listing.shortDescription,
    listing.fullDescription,
  ].find((value) => value != null && value.trim().length > 0);

  if (!candidate) return '—';

  return candidate.length > 80 ? `${candidate.slice(0, 80)}…` : candidate;
}

/** Absolute short date, or an em dash when never published. */
function formatWhen(iso: string | null): string {
  if (!iso) return '—';

  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Listings table + editor/publish flows for one platform. */
export function ListingsPanel({ platform }: { platform: StorePlatform }): JSX.Element {
  const { data, isLoading, isError, error, refetch } = useStoreListings();
  const publish = usePublishListing();

  const [newOpen, setNewOpen] = useState(false);
  const [drawer, setDrawer] = useState<{ locale: string; listing?: StoreListing } | null>(null);
  const [publishTarget, setPublishTarget] = useState<StoreListing | null>(null);

  const rows = useMemo(
    () => (data ?? []).filter((l) => l.platform === platform),
    [data, platform],
  );
  const existingLocales = useMemo(() => rows.map((r) => r.locale), [rows]);

  function confirmPublish(options: PublishOptions): void {
    if (!publishTarget) return;
    const { locale } = publishTarget;

    publish.mutate(
      { platform, locale, options },
      {
        onSuccess: () => {
          toast.success(`Published ${PLATFORM_LABELS[platform]} · ${locale}.`);
          setPublishTarget(null);
        },
        onError: notifyPublishError,
      },
    );
  }

  const action = (
    <Button size="sm" onClick={() => setNewOpen(true)}>
      <Plus className="h-4 w-4" aria-hidden />
      New listing
    </Button>
  );

  return (
    <SectionCard
      title="Listings"
      description="Store copy per locale. Editing saves a draft; publishing pushes it to the store."
      action={action}
    >
      {isLoading && <TableSkeleton rows={4} label="Loading listings" />}

      {!isLoading && isError && !data && (
        <ErrorState title="Couldn't load listings." error={error} onRetry={() => void refetch()} />
      )}

      {!isLoading && data && rows.length === 0 && (
        <EmptyState
          message={`No ${PLATFORM_LABELS[platform]} listings yet`}
          action={action}
        />
      )}

      {!isLoading && rows.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Locale</TableHead>
              <TableHead>Summary</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last published</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-mono">{row.locale}</TableCell>
                <TableCell className="max-w-md text-muted-foreground">{summaryFor(row)}</TableCell>
                <TableCell>
                  <ListingStatusBadge status={row.status} />
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatWhen(row.lastPublishedAt)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDrawer({ locale: row.locale, listing: row })}
                    >
                      Edit
                    </Button>
                    <Button size="sm" onClick={() => setPublishTarget(row)}>
                      Publish
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <NewListingDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        platform={platform}
        existingLocales={existingLocales}
        onCreate={(locale) => setDrawer({ locale, listing: undefined })}
      />

      <ListingDrawer
        open={drawer !== null}
        onOpenChange={(open) => !open && setDrawer(null)}
        platform={platform}
        locale={drawer?.locale ?? ''}
        listing={drawer?.listing}
      />

      <PublishConfirmDialog
        open={publishTarget !== null}
        onOpenChange={(open) => !open && setPublishTarget(null)}
        platform={platform}
        target={`the ${publishTarget?.locale ?? ''} listing`}
        isPending={publish.isPending}
        onConfirm={confirmPublish}
      />
    </SectionCard>
  );
}
